/**
 * Overview metrics component for instructor dashboard
 */

import React from 'react';
import { MetricsCard } from '../admin/MetricsCard';
import { InstructorOverviewStats } from '../../services/instructor';

interface OverviewMetricsProps {
  stats: InstructorOverviewStats | null;
  loading?: boolean;
}

export function OverviewMetrics({ stats, loading = false }: OverviewMetricsProps) {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <MetricsCard title="Active Runs" value="--" loading={true} />
        <MetricsCard title="Active Users" value="--" loading={true} />
        <MetricsCard title="Recent Grades" value="--" loading={true} />
        <MetricsCard title="System Average" value="--" loading={true} />
      </div>
    );
  }

  const systemAverage =
    stats.average_system_grade !== undefined && stats.average_system_grade !== null
      ? `${stats.average_system_grade.toFixed(1)}%`
      : 'N/A';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <MetricsCard
        title="Active Runs"
        value={stats.active_runs}
        subtitle="Currently running lab instances"
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        }
        className="bg-gradient-to-br from-blue-500/20 to-blue-700/20 border-blue-500/30"
      />
      
      <MetricsCard
        title="Active Users"
        value={stats.users_with_runs}
        subtitle={`of ${stats.total_users} total users`}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
        }
        className="bg-gradient-to-br from-green-500/20 to-green-700/20 border-green-500/30"
      />
      
      <MetricsCard
        title="Recent Grades"
        value={stats.recent_grades}
        subtitle="in the last 24 hours"
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
          </svg>
        }
        className="bg-gradient-to-br from-purple-500/20 to-purple-700/20 border-purple-500/30"
      />
      
      <MetricsCard
        title="System Average"
        value={systemAverage}
        subtitle={`${stats.overridden_grades} grades overridden`}
        icon={
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        }
        className="bg-gradient-to-br from-orange-500/20 to-orange-700/20 border-orange-500/30"
      />
    </div>
  );
}
